import React, { useEffect, useMemo, useRef, useState } from 'react'

import { BeltVisual } from '@goodfellas/belt-visual'
import { API_URL } from '@goodfellas/environment'
import { Colors, list as degreeList } from '@goodfellas/graduation'

import { goodfellasApi } from '../../../apis'
import BeltSelect from '../../belt/BeltSelect'

type GraduationModalProps = {
  isOpen: boolean
  phoneNumber: string
  groupId: string
  initialName?: string
  initialType?: string
  initialBelt: Colors
  initialDegree: number
  onClose: () => void
  onSaved: (next: { belt: Colors; degree: number }) => void
}

export default function GraduationModal({
  isOpen,
  phoneNumber,
  groupId,
  initialName,
  initialType,
  initialBelt,
  initialDegree,
  onClose,
  onSaved,
}: GraduationModalProps) {
  const cancelRef = useRef<HTMLButtonElement>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [belt, setBelt] = useState<Colors>(initialBelt)
  const [degree, setDegree] = useState<number>(initialDegree ?? 0)

  useEffect(() => {
    if (!isOpen) return
    setError(null)
    setSaving(false)
    setBelt(initialBelt)
    setDegree(initialDegree ?? 0)
    const id = window.setTimeout(() => cancelRef.current?.focus(), 0)
    return () => window.clearTimeout(id)
  }, [isOpen, initialBelt, initialDegree])

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        if (!saving) onClose()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose, saving])

  const allowedDegrees = useMemo(() => {
    const arr = degreeList[belt as Colors] as readonly number[] | undefined
    if (!arr || arr.length === 0) return [0]
    return arr
  }, [belt])

  useEffect(() => {
    if (!allowedDegrees.includes(degree)) {
      setDegree(allowedDegrees[0] ?? 0)
    }
  }, [allowedDegrees, degree])

  const save = async () => {
    setError(null)
    setSaving(true)
    try {
      const payload = {
        phoneNumber,
        name: initialName || '',
        belt,
        degree,
        type: initialType || '',
        groupId,
      }

      const response = await goodfellasApi.post(
        `${API_URL}evaluation/tables`,
        payload,
        {
          validateStatus: () => true,
        },
      )

      if (response.status >= 200 && response.status < 300) {
        onSaved({ belt, degree })
        onClose()
        return
      }

      setError('Não foi possível salvar a graduação. Tente novamente.')
    } catch (e) {
      console.error('Error saving graduation:', e)
      setError('Não foi possível salvar a graduação. Tente novamente.')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000]"
      role="dialog"
      aria-modal="true"
      aria-label="Editar graduação"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose()
      }}
    >
      <div className="h-full w-full flex items-center justify-center px-4 py-10">
        <div className="bg-gray-50 rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-6 py-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-extrabold text-white leading-tight">
                  Editar graduação
                </h2>
                <p className="text-white/70 text-sm mt-1">
                  Atualize sua faixa e o grau/stripes.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-xl transition-colors disabled:opacity-50"
                aria-label="Fechar"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
                {error}
              </div>
            )}

            <div className="bg-white rounded-2xl p-4 border border-gray-100 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="text-gray-900 font-black tracking-tight">
                  Pré-visualização
                </div>
                <div className="text-xs text-gray-500 font-semibold">
                  {initialName ? initialName : phoneNumber}
                </div>
              </div>
              <div className="flex-shrink-0">
                <BeltVisual color={belt} degree={degree} size="md" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Faixa
              </label>
              <BeltSelect
                value={belt}
                onChange={(c) => {
                  // If the athlete changes belt, degrees/stripes must reset.
                  setBelt(c)
                  setDegree(0)
                }}
                labels={{
                  choose: 'Selecione aqui',
                  searchPlaceholder: 'Buscar faixa...',
                  white: 'Branca',
                  blue: 'Azul',
                  purple: 'Roxa',
                  brown: 'Marrom',
                  black: 'Preta',
                  redBlack: 'Coral Vermelha e Preta',
                  redWhite: 'Coral Vermelha e Branca',
                  red: 'Vermelha',
                  gray: 'Cinza',
                  yellow: 'Amarela',
                  orange: 'Laranja',
                  green: 'Verde',
                  juniorGroup: 'Infantil',
                  adultGroup: 'Adulto',
                  professionalGroup: 'Profissional',
                }}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Grau
              </label>
              <select
                value={degree}
                onChange={(e) => setDegree(Number(e.target.value))}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all duration-200 text-gray-900"
              >
                {allowedDegrees.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 pb-6">
            <div className="flex gap-3">
              <button
                type="button"
                ref={cancelRef}
                onClick={onClose}
                disabled={saving}
                className="flex-1 px-5 py-3.5 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-100 transition-colors font-semibold disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="flex-1 px-5 py-3.5 bg-gradient-to-r from-red-600 to-red-500 text-white rounded-xl hover:from-red-700 hover:to-red-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-semibold shadow-lg shadow-red-500/25"
              >
                {saving && (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                )}
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
